/**
 * The built-in fields Add/Edit Employee collects, organized the same way the wizard itself already
 * is: Personal Information, Work Information, Bank Information. This is what the "Employee Fields"
 * admin page (Organisation → Employee Fields) renders instead of being empty, and what an org's
 * "Required" override for a built-in field is checked against on create
 * (`apps/api/src/routes/employee.ts`'s `assertBuiltInFieldsRequired`).
 *
 * A SEPARATE, narrower concern lives in `profile-fields.ts`: which of these fields an EMPLOYEE can
 * edit about THEMSELVES, and whether that self-service edit applies instantly or needs HR review.
 * Only fields marked `selfService` here overlap with that registry at all — job facts (department,
 * grade, shift, manager, contract dates) are never self-editable in any form, matching
 * profile-fields.ts's own header comment. `EmployeeFieldPolicy.requiresApproval` (the per-org
 * override of the instant/approval rule) is only meaningful, and only ever shown as configurable,
 * for a `selfService: true` field.
 */

export const BUILT_IN_EMPLOYEE_FIELD_SECTIONS = ['personal', 'work', 'bank'] as const;
export type BuiltInEmployeeFieldSection = (typeof BUILT_IN_EMPLOYEE_FIELD_SECTIONS)[number];

/** Display names, matching the Add Employee wizard's step titles. */
export const BUILT_IN_EMPLOYEE_FIELD_SECTION_LABELS: Record<BuiltInEmployeeFieldSection, string> = {
  personal: 'Personal Information',
  work: 'Work Information',
  bank: 'Bank Information',
};

export function isBuiltInEmployeeFieldSection(key: string): key is BuiltInEmployeeFieldSection {
  return (BUILT_IN_EMPLOYEE_FIELD_SECTIONS as readonly string[]).includes(key);
}

export interface BuiltInEmployeeFieldSpec {
  /** The key in createEmployeeBodySchema/updateEmployeeBodySchema — dotted for a bankAccount
   *  sub-field (e.g. "bankAccount.bankName"), since that's how Add/Edit Employee nests it. */
  key: string;
  label: string;
  section: BuiltInEmployeeFieldSection;
  /** Always required — the system depends on it (sign-in, or a NOT NULL database column). Not
   *  admin-toggleable; the "Required" control renders locked/on for these. */
  locked?: boolean;
  /** Also has a self-service edit path (see profile-fields.ts) — eligible for the "needs HR
   *  approval" toggle, not just "required". */
  selfService?: boolean;
  /** The key `profile-fields.ts` (and `updateMyProfile`) uses for this same field, when it
   *  differs from `key` above. Only the four self-service-eligible bank fields differ: Add/Edit
   *  Employee nests them under `bankAccount.*`, but self-service writes flat `BankAccount`
   *  columns directly, with no nesting. Defaults to `key` when omitted. */
  selfServiceKey?: string;
}

export const BUILT_IN_EMPLOYEE_FIELDS: readonly BuiltInEmployeeFieldSpec[] = [
  // --- Personal Information (Add Employee wizard, step 1) --------------------------------------
  { key: 'firstName', label: 'First Name', section: 'personal', locked: true, selfService: true },
  { key: 'lastName', label: 'Last Name', section: 'personal', locked: true, selfService: true },
  { key: 'email', label: 'Work Email', section: 'personal', locked: true },
  { key: 'username', label: 'Username', section: 'personal' },
  { key: 'phone', label: 'Phone', section: 'personal', selfService: true },
  { key: 'personalEmail', label: 'Personal Email', section: 'personal', selfService: true },
  { key: 'workPhone', label: 'Work Phone (secondary)', section: 'personal' },
  { key: 'gender', label: 'Gender', section: 'personal', selfService: true },
  { key: 'dateOfBirth', label: 'Date of Birth', section: 'personal', selfService: true },
  { key: 'addressLine1', label: 'Address', section: 'personal', selfService: true },
  { key: 'city', label: 'City', section: 'personal', selfService: true },
  { key: 'stateCode', label: 'State', section: 'personal', selfService: true },
  { key: 'postalCode', label: 'Postal Code', section: 'personal', selfService: true },
  { key: 'country', label: 'Country', section: 'personal', selfService: true },
  { key: 'qualification', label: 'Qualification', section: 'personal' },
  { key: 'experience', label: 'Experience', section: 'personal' },
  { key: 'maritalStatus', label: 'Marital Status', section: 'personal', selfService: true },
  { key: 'childrenCount', label: 'Number of Children', section: 'personal' },
  {
    key: 'emergencyContactName',
    label: 'Emergency Contact Name',
    section: 'personal',
    selfService: true,
  },
  {
    key: 'emergencyContactPhone',
    label: 'Emergency Contact Phone',
    section: 'personal',
    selfService: true,
  },
  {
    key: 'emergencyContactRelation',
    label: 'Emergency Contact Relationship',
    section: 'personal',
    selfService: true,
  },

  // --- Work Information (step 2) ----------------------------------------------------------------
  { key: 'legalEntityId', label: 'Legal Entity', section: 'work', locked: true },
  { key: 'employmentType', label: 'Employment Type', section: 'work', locked: true },
  { key: 'joinedOn', label: 'Joining Date', section: 'work', locked: true },
  { key: 'departmentId', label: 'Department', section: 'work' },
  { key: 'locationId', label: 'Work Location', section: 'work' },
  { key: 'managerEmploymentId', label: 'Reporting Manager', section: 'work' },
  { key: 'designation', label: 'Designation', section: 'work' },
  /** Presence is `gradeId || grade` — a create body carries one or the other, never both
   *  (ADR 0022: catalogue grade when the org has one, else the free-text field). */
  { key: 'grade', label: 'Grade', section: 'work' },
  { key: 'costCentreId', label: 'Cost Centre', section: 'work' },
  { key: 'shiftId', label: 'Shift', section: 'work' },
  { key: 'jobRole', label: 'Job Role', section: 'work' },
  { key: 'workType', label: 'Work Type', section: 'work' },
  { key: 'contractEndOn', label: 'Contract End Date', section: 'work' },

  // --- Bank Information (step 3) ------------------------------------------------------------------
  {
    key: 'bankAccount.bankName',
    label: 'Bank Name',
    section: 'bank',
    selfService: true,
    selfServiceKey: 'bankName',
  },
  {
    key: 'bankAccount.accountNumber',
    label: 'Account Number',
    section: 'bank',
    selfService: true,
    selfServiceKey: 'accountNumber',
  },
  {
    key: 'bankAccount.branch',
    label: 'Branch',
    section: 'bank',
    selfService: true,
    selfServiceKey: 'branch',
  },
  {
    key: 'bankAccount.bankCode1',
    label: 'IFSC / Bank Code',
    section: 'bank',
    selfService: true,
    selfServiceKey: 'bankCode1',
  },
  { key: 'bankAccount.bankCode2', label: 'Secondary Bank Code', section: 'bank' },
  { key: 'bankAccount.accountType', label: 'Account Type', section: 'bank' },
  { key: 'bankAccount.addressLine1', label: 'Bank Address', section: 'bank' },
  { key: 'bankAccount.city', label: 'Bank City', section: 'bank' },
  { key: 'bankAccount.stateCode', label: 'Bank State', section: 'bank' },
  { key: 'bankAccount.country', label: 'Bank Country', section: 'bank' },
];

export const BUILT_IN_EMPLOYEE_FIELD_MAP: ReadonlyMap<string, BuiltInEmployeeFieldSpec> = new Map(
  BUILT_IN_EMPLOYEE_FIELDS.map((field) => [field.key, field]),
);

/** Every field this registry marks self-service-eligible, keyed by ITS profile-fields.ts key. */
export const SELF_SERVICE_BUILT_IN_FIELDS: readonly BuiltInEmployeeFieldSpec[] =
  BUILT_IN_EMPLOYEE_FIELDS.filter((field) => field.selfService);
